import { useEffect, useRef, useState } from 'react'
import { useValues } from '@core/store'
import type { TAudioSimple } from '@core/models'

import { audioPlayerStore } from '../store/audioPlayerStore'
import { useAudioRef } from '../providers/AudioRefProvider'

import { useActiveAudio } from './useActiveAudio'

/** Current playback position (seconds) for `audioId`, driven by rAF while it is the active track. */
export const usePlayerTime = (audioId: TAudioSimple['id']): number => {
    const audioRef = useAudioRef()
    const activeAudio = useActiveAudio()
    const [isPlaying] = useValues([audioPlayerStore.props.getIsPlaying])
    const [timeProgress, setTimeProgress] = useState(0)

    const playAnimationRef = useRef<number | undefined>(undefined)
    const isActiveAudio = activeAudio?.id === audioId
    const shouldListen = isActiveAudio && isPlaying

    useEffect(() => {
        const updateTime = () => {
            const currentTime = audioRef.current?.currentTime
            if (currentTime && shouldListen) {
                setTimeProgress(currentTime)
            }
            if (shouldListen) {
                playAnimationRef.current = requestAnimationFrame(updateTime)
            }
        }

        if (shouldListen) {
            playAnimationRef.current = requestAnimationFrame(updateTime)
        }

        return () => {
            if (playAnimationRef.current) {
                cancelAnimationFrame(playAnimationRef.current)
                playAnimationRef.current = undefined
            }
        }
    }, [shouldListen, audioRef])

    useEffect(() => {
        if (!isActiveAudio) {
            setTimeProgress(0)
        }
    }, [isActiveAudio])

    return isActiveAudio ? timeProgress : 0
}
